
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  order_title: string;
  order_description: string;
  status: string;
  price?: number;
  paid?: boolean;
  product_title?: string;
  product_description?: string;
}

interface Recommendation {
  id: string;
  title: string;
  description: string;
  price: number;
  image_url: string | null;
}

interface CartItem {
  id: string;
  order_id?: string;
  recommendation_id?: string;
  customer_email: string;
  added_at: string;
}

interface ProfileData {
  shipping_address: string;
  shipping_city: string;
  shipping_district: string;
  shipping_postal_code: string;
  shipping_phone: string;
  billing_same_as_shipping: boolean;
  billing_name: string;
  billing_address: string;
  billing_city: string;
  billing_district: string;
  billing_postal_code: string;
  billing_tax_office: string;
  billing_tax_number: string;
}

export default function CartPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentError, setPaymentError] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const navigate = useNavigate();
  const customerEmail = localStorage.getItem('customerEmail');

  useEffect(() => {
    if (!customerEmail) {
      navigate('/customer-login');
      return;
    }
    fetchData();
  }, [customerEmail, navigate]);

  const fetchData = async () => {
    try {
      const { data: cartData, error: cartError } = await supabase
        .from('cart')
        .select('*')
        .eq('customer_email', customerEmail);

      if (cartError) throw cartError;

      setCartItems(cartData || []);

      const orderCartItems = (cartData || []).filter(item => item.order_id);
      const recommendationCartItems = (cartData || []).filter(item => item.recommendation_id);

      // Sipariş ürünlerini çek
      if (orderCartItems.length > 0) {
        const orderIds = orderCartItems.map(item => item.order_id);
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .in('id', orderIds);

        if (ordersError) throw ordersError;

        if (!ordersData || ordersData.length === 0) {
          await supabase.from('cart').delete().eq('customer_email', customerEmail).not('order_id', 'is', null);
          setOrders([]);
        } else {
          setOrders(ordersData);
        }
      } else {
        setOrders([]);
      }

      // Öneri ürünlerini çek
      if (recommendationCartItems.length > 0) {
        const recIds = recommendationCartItems.map(item => item.recommendation_id);
        const { data: recsData, error: recsError } = await supabase
          .from('recommendations')
          .select('*')
          .in('id', recIds);

        if (recsError) throw recsError;
        setRecommendations(recsData || []);
      } else {
        setRecommendations([]);
      }

      // Profil bilgilerini al
      const { data: profileData, error: profileError } = await supabase
        .from('customer_profiles')
        .select('*')
        .eq('customer_email', customerEmail)
        .maybeSingle();

      if (!profileError && profileData) {
        setProfile({
          shipping_address: profileData.shipping_address || '',
          shipping_city: profileData.shipping_city || '',
          shipping_district: profileData.shipping_district || '',
          shipping_postal_code: profileData.shipping_postal_code || '',
          shipping_phone: profileData.shipping_phone || '',
          billing_same_as_shipping: profileData.billing_same_as_shipping ?? true,
          billing_name: profileData.billing_name || '',
          billing_address: profileData.billing_address || '',
          billing_city: profileData.billing_city || '',
          billing_district: profileData.billing_district || '',
          billing_postal_code: profileData.billing_postal_code || '',
          billing_tax_office: profileData.billing_tax_office || '',
          billing_tax_number: profileData.billing_tax_number || '',
        });
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeOrderFromCart = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('cart')
        .delete()
        .eq('order_id', orderId)
        .eq('customer_email', customerEmail);

      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error('Sepetten silinirken hata:', error);
    }
  };

  const removeRecommendationFromCart = async (recommendationId: string) => {
    try {
      const { error } = await supabase
        .from('cart')
        .delete()
        .eq('recommendation_id', recommendationId)
        .eq('customer_email', customerEmail);

      if (error) throw error;
      await fetchData();
    } catch (error) {
      console.error('Sepetten silinirken hata:', error);
    }
  };

  const getCartTotal = () => {
    const ordersTotal = orders.reduce((total, order) => total + (order.price || 0), 0);
    const recsTotal = recommendations.reduce((total, rec) => total + (rec.price || 0), 0);
    return ordersTotal + recsTotal;
  };

  const totalItemCount = orders.length + recommendations.length;

  const handlePayment = () => {
    if (!agreementAccepted) {
      setPaymentError('Devam etmek için Mesafeli Satış Sözleşmesini onaylamanız gerekmektedir.');
      return;
    }

    if (totalItemCount === 0) {
      setPaymentError('Sepetinizde ödeme yapılacak ürün bulunamadı');
      return;
    }

    navigate('/bank-transfer');
  };

  const getFullAddress = () => {
    if (!profile) return null;
    const parts = [];
    if (profile.shipping_address) parts.push(profile.shipping_address);
    if (profile.shipping_district) parts.push(profile.shipping_district);
    if (profile.shipping_city) parts.push(profile.shipping_city);
    if (profile.shipping_postal_code) parts.push(profile.shipping_postal_code);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-green-500 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium">Siparişlerime Dön</span>
          </button>
          <div className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png"
              alt="Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <i className="ri-shopping-cart-2-line text-green-500"></i>
          Sepetim
          {totalItemCount > 0 && (
            <span className="bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full font-medium">
              {totalItemCount} Ürün
            </span>
          )}
        </h1>

        {totalItemCount === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sepetiniz Boş</h2>
            <p className="text-gray-500 mb-6">Fiyatı belirlenen siparişlerinizi veya önerilen ürünleri sepete ekleyerek ödeme yapabilirsiniz.</p>
            <button
              onClick={() => navigate('/customer-dashboard')}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Siparişlerime Git
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sol Taraf - Ürün Listesi */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-box-3-line text-green-500"></i>
                    Sepetteki Ürünler
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {/* Sipariş Ürünleri */}
                  {orders.map((order) => (
                    <div key={order.id} className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-2xl text-green-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-gray-900 font-semibold text-base">
                              {order.product_title || order.order_title}
                            </h3>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                              Sipariş
                            </span>
                          </div>
                          {order.product_description && (
                            <p className="text-gray-600 text-sm mb-2 line-clamp-2">{order.product_description}</p>
                          )}
                          {!order.product_description && order.order_description && (
                            <p className="text-gray-500 text-sm mb-2 line-clamp-2">{order.order_description}</p>
                          )}
                          <p className="text-gray-400 text-xs">Sipariş No: #{order.id.slice(0, 8).toUpperCase()}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-green-600 font-bold text-xl mb-2">{order.price?.toFixed(2)} ₺</p>
                          <button
                            onClick={() => removeOrderFromCart(order.id)}
                            className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1 cursor-pointer ml-auto transition-colors"
                          >
                            <i className="ri-delete-bin-line"></i>
                            Kaldır
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Öneri Ürünleri */}
                  {recommendations.map((rec) => (
                    <div key={rec.id} className="p-4">
                      <div className="flex items-start gap-4">
                        {rec.image_url ? (
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                            <img src={rec.image_url} alt={rec.title} className="w-full h-full object-cover object-top" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="ri-lightbulb-line text-2xl text-amber-500"></i>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-gray-900 font-semibold text-base">{rec.title}</h3>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap flex items-center gap-1">
                              <i className="ri-lightbulb-line text-xs"></i>
                              Önerilen Ürün
                            </span>
                          </div>
                          {rec.description && (
                            <p className="text-gray-600 text-sm mb-2 line-clamp-2">{rec.description}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-green-600 font-bold text-xl mb-2">{rec.price?.toFixed(2)} ₺</p>
                          <button
                            onClick={() => removeRecommendationFromCart(rec.id)}
                            className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1 cursor-pointer ml-auto transition-colors"
                          >
                            <i className="ri-delete-bin-line"></i>
                            Kaldır
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Teslimat Adresi */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-map-pin-line text-green-500"></i>
                    Teslimat Adresi
                  </h2>
                  <button
                    onClick={() => navigate('/customer-dashboard')}
                    className="text-green-600 hover:text-green-700 text-sm font-medium cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-edit-line"></i>
                    Düzenle
                  </button>
                </div>
                <div className="p-4">
                  {getFullAddress() ? (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-home-4-line text-green-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium mb-1">Kargo Adresi</p>
                        <p className="text-gray-600 text-sm">{getFullAddress()}</p>
                        {profile?.shipping_phone && (
                          <p className="text-gray-500 text-sm mt-1">
                            <i className="ri-phone-line mr-1"></i>
                            {profile.shipping_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-error-warning-line text-amber-500 text-xl mt-0.5"></i>
                        <div>
                          <p className="text-amber-800 font-medium text-sm">Adres bilgisi bulunamadı</p>
                          <p className="text-amber-700 text-xs mt-1">Profil bilgilerinizden teslimat adresinizi ekleyebilirsiniz.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Özet ve Ödeme */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-24">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-green-500"></i>
                    Sipariş Özeti
                  </h2>
                </div>
                <div className="p-4 space-y-4">
                  <div className="space-y-2">
                    {orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 truncate flex-1 pr-2">
                          {order.product_title || order.order_title}
                        </span>
                        <span className="text-gray-900 font-medium whitespace-nowrap">{order.price?.toFixed(2)} ₺</span>
                      </div>
                    ))}
                    {recommendations.map((rec) => (
                      <div key={rec.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 truncate flex-1 pr-2 flex items-center gap-1">
                          <i className="ri-lightbulb-line text-amber-500 flex-shrink-0"></i>
                          {rec.title}
                        </span>
                        <span className="text-gray-900 font-medium whitespace-nowrap">{rec.price?.toFixed(2)} ₺</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 font-semibold text-lg">Toplam</span>
                      <span className="text-green-600 font-bold text-2xl">{getCartTotal().toFixed(2)} ₺</span>
                    </div>
                  </div>

                  {/* Sözleşme Onayı */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreementAccepted}
                        onChange={(e) => {
                          setAgreementAccepted(e.target.checked);
                          if (e.target.checked) setPaymentError('');
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 flex-shrink-0"
                      />
                      <span className="text-gray-700 text-sm leading-relaxed">
                        <a
                          href="https://xkartvizitler.com/mesafeli-satis-sozlesmesi/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 underline font-medium"
                        >
                          Ticari Nitelikli Mesafeli Satış Sözleşmesi
                        </a>
                        &apos;ni okudum, onaylıyorum.
                      </span>
                    </label>
                  </div>

                  {paymentError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                      <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                      <span>{paymentError}</span>
                    </div>
                  )}

                  <button
                    onClick={handlePayment}
                    className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-green-500/25 whitespace-nowrap"
                  >
                    <i className="ri-bank-line text-xl"></i>
                    Havale / EFT ile Öde
                  </button>

                  <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                    <i className="ri-shield-check-line text-green-500"></i>
                    <span>Güvenli ödeme</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
